/**
 * UNE seule réponse à « quelle est l'image de cet article ? », lue par la
 * liste du blog, la page article, l'image de partage (Open Graph), les
 * données structurées et le plan du site.
 *
 * Avant (relevé du 29/09/2026), chaque endroit décidait seul : la liste et la
 * page essayaient /images/articles/{slug}.webp même quand le fichier n'existe
 * pas (404, puis un emoji ou un cadre vide), l'Open Graph ignorait ces images
 * statiques et servait la carte blanche générique /api/og à 120 articles sur
 * 123, et le plan du site n'annonçait aucune image.
 *
 * Ordre : image à la une (médiathèque) → image statique de l'ancien site
 * (seulement si le fichier existe, images-statiques.ts) → rien. La vignette
 * (800 × 450, couvertures générées) sert aux cartes de la liste ; à défaut,
 * l'image à la une.
 */
import { aImageStatique, urlImageStatique } from './images-statiques'

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL || 'https://www.stock-co2.com'

interface MediaLike {
  url?: string | null
  width?: number | null
  height?: number | null
  alt?: string | null
}

export interface ArticleAvecImage {
  slug: string
  title?: string | null
  featuredImage?: unknown
  vignette?: unknown
  seo?: { metaImage?: unknown } | null
}

const media = (v: unknown): MediaLike | null =>
  v && typeof v === 'object' && typeof (v as MediaLike).url === 'string' && (v as MediaLike).url ? (v as MediaLike) : null

export interface Couverture {
  /** Image principale (page article), chemin relatif ou URL absolue. */
  url: string
  /** Image des cartes de liste. */
  vignette: string
  largeur: number | null
  hauteur: number | null
  source: 'mediatheque' | 'statique'
}

export function couvertureArticle(a: ArticleAvecImage): Couverture | null {
  const principale = media(a.featuredImage)
  if (principale) {
    return {
      url: principale.url!,
      vignette: media(a.vignette)?.url || principale.url!,
      largeur: principale.width ?? null,
      hauteur: principale.height ?? null,
      source: 'mediatheque',
    }
  }
  if (aImageStatique(a.slug)) {
    const u = urlImageStatique(a.slug)
    return { url: u, vignette: u, largeur: null, hauteur: null, source: 'statique' }
  }
  return null
}

export const absolue = (u: string): string => (/^https?:\/\//.test(u) ? u : `${SITE_URL}${u.startsWith('/') ? '' : '/'}${u}`)

/**
 * Image de partage : l'image OG choisie à la main (SEO), sinon la couverture,
 * sinon la carte générée /api/og avec le titre. Toujours une URL absolue.
 */
export function imagePartage(a: ArticleAvecImage): { url: string; largeur: number | null; hauteur: number | null } {
  const og = media(a.seo?.metaImage)
  if (og) return { url: absolue(og.url!), largeur: og.width ?? null, hauteur: og.height ?? null }
  const c = couvertureArticle(a)
  if (c) return { url: absolue(c.url), largeur: c.largeur, hauteur: c.hauteur }
  return { url: `${SITE_URL}/api/og?title=${encodeURIComponent(a.title || 'Stock CO2')}`, largeur: 1200, hauteur: 630 }
}
